import React, { useState } from 'react';
import { Modal, Descriptions, Tag, Button, Space, Select, Input, message, Divider } from 'antd';
import {
  UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined,
  MessageOutlined, CalendarOutlined, TagOutlined
} from '@ant-design/icons';
import { format } from 'date-fns';

const { Option } = Select;
const { TextArea } = Input;

const personaLabels = {
  company: 'Company',
  association: 'Association',
  startup: 'Startup',
  incubator: 'Incubator',
  investor: 'Investor',
  academia: 'Academia',
  consultant: 'Consultant',
};

const ContactRequestDetailModal = ({ request, isOpen, onClose, onStatusUpdate }) => {
  const [status, setStatus] = useState(request?.status || 'pending');
  const [response, setResponse] = useState(request?.response || '');
  const [updating, setUpdating] = useState(false);

  if (!request) return null;

  const getStatusConfig = (status) => {
    const configs = {
      'pending': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'Pending' },
      'in-progress': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'In Progress' },
      'resolved': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'Resolved' },
      'closed': { color: '#8c8c8c', bg: '#fafafa', border: '#d9d9d9', label: 'Closed' },
      'rejected': { color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', label: 'Rejected' },
    };
    return configs[status] || configs['pending'];
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    try {
      await onStatusUpdate(request._id, { status, response });
    } catch (error) {
      message.error('Failed to update request');
    } finally {
      setUpdating(false);
    }
  };

  const statusConfig = getStatusConfig(status);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <MessageOutlined style={{ fontSize: 24, color: '#470172' }} />
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '20px', fontWeight: 500, color: '#470172' }}>
            Contact AID Office Request
          </span>
        </div>
      }
      width={700}
      footer={[
        <Button
          key="close"
          onClick={onClose}
          style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }}
        >
          Close
        </Button>,
        <Button
          key="update"
          type="primary"
          loading={updating}
          onClick={handleUpdateStatus}
          disabled={status === request.status && response === (request.response || '')}
          style={{
            fontFamily: 'Rubik, sans-serif',
            fontSize: '16px',
            color: '#ffffff',
            fontWeight: 400,
            backgroundColor: '#470172',
            borderColor: '#470172'
          }}
        >
          Update Request
        </Button>,
      ]}
      style={{ top: 20 }}
    >
      <div style={{ fontFamily: 'Rubik, sans-serif' }}>
        {/* Status Update Section */}
        <div style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <label style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 500, color: '#470172', display: 'block', marginBottom: 8 }}>
                Update Status
              </label>
              <Select
                value={status}
                onChange={setStatus}
                style={{ width: '100%', fontFamily: 'Rubik, sans-serif' }}
                size="large"
              >
                <Option value="pending">Pending</Option>
                <Option value="in-progress">In Progress</Option>
                <Option value="resolved">Resolved</Option>
                <Option value="closed">Closed</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
            </div>
            <div>
              <label style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 500, color: '#470172', display: 'block', marginBottom: 8 }}>
                Admin Response
              </label>
              <TextArea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Add a response for this request..."
                rows={3}
                style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px' }}
              />
            </div>
          </Space>
        </div>

        <Divider />

        {/* Request Details */}
        <Descriptions
          column={1}
          labelStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 500, color: '#470172' }}
          contentStyle={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', color: '#595959' }}
        >
          <Descriptions.Item label={<><UserOutlined style={{ marginRight: 8 }} />Contact Name</>}>
            {request.contactName || '—'}
          </Descriptions.Item>

          <Descriptions.Item label={<><HomeOutlined style={{ marginRight: 8 }} />Company</>}>
            {request.companyId?.companyName || 'Unknown Company'}
          </Descriptions.Item>

          <Descriptions.Item label="Persona">
            <Tag color="blue">{personaLabels[request.personaType] || 'Company'}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label={<><MailOutlined style={{ marginRight: 8 }} />Email</>}>
            <a href={`mailto:${request.email}`} style={{ color: '#470172' }}>{request.email}</a>
          </Descriptions.Item>

          <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 8 }} />Phone</>}>
            <a href={`tel:${request.phone}`} style={{ color: '#470172' }}>{request.phone}</a>
          </Descriptions.Item>

          <Descriptions.Item label={<><TagOutlined style={{ marginRight: 8 }} />Category</>}>
            <Tag color="blue">{request.category}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Subject">
            {request.subject}
          </Descriptions.Item>

          <Descriptions.Item label={<><MessageOutlined style={{ marginRight: 8 }} />Message</>}>
            <div style={{ whiteSpace: 'pre-wrap' }}>{request.message}</div>
          </Descriptions.Item>

          <Descriptions.Item label="Current Status">
            <Tag
              style={{
                fontSize: 13,
                fontFamily: "'Rubik', sans-serif",
                fontWeight: 600,
                border: `1px solid ${statusConfig.border}`,
                background: statusConfig.bg,
                color: statusConfig.color,
                padding: '4px 14px',
                borderRadius: 6,
              }}
            >
              {statusConfig.label}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 8 }} />Submitted On</>}>
            {request.createdAt ? format(new Date(request.createdAt), 'MMMM d, yyyy h:mm a') : '—'}
          </Descriptions.Item>

          {request.resolvedAt && (
            <Descriptions.Item label="Resolved On">
              {format(new Date(request.resolvedAt), 'MMMM d, yyyy h:mm a')}
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>
    </Modal>
  );
};

export default ContactRequestDetailModal;
